import { useQuery } from "@apollo/client";
import { getSeasonAvatarPath } from "enefel/avatarsSeason";
import { FC } from "react";
import styled from "styled-components";
import publicPath from "../../helpers/publicPath";
import { useIsAdmin } from "../../hooks/useIsAdmin";
import { GET_SEASONAL_SALES_ADMIN } from "../../query/StoreQueries";
import { StyledChartContainer, StyledSection } from "../Chart/common/styles";
import Error from "../Error/QueryError";
import Loading from "../Loading";
import { H1 } from "../Title";

type AvatarSales = {
  file: string;
  itemKey: string;
  rarity: string;
  price: number;
  portrait: boolean;
  sales: number;
  revenue: number;
};

type DiceSales = {
  id: string;
  color: string;
  background: string;
  price: number;
  sales: number;
  revenue: number;
};

type SeasonSales = {
  season: number;
  isCurrent: boolean;
  avatars: AvatarSales[];
  dice: DiceSales[];
  avatarSales: number;
  diceSales: number;
  revenue: number;
};

const Wrapper = styled.div`
  padding: 1rem;
`;

/* Le body n'a pas de fond défini : sans panneau sombre le texte blanc serait
   illisible. Même parti pris que les autres écrans admin. */
const Panel = styled.div`
  background: rgba(0, 0, 0, 0.85);
  border: 1px solid #444;
  border-radius: 8px;
  color: white;
  margin-bottom: 1.5rem;
  padding: 1rem;
`;

const SeasonHeader = styled.div`
  align-items: baseline;
  border-bottom: 1px solid #444;
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin-bottom: 1rem;
  padding-bottom: 0.5rem;

  h2 {
    color: white;
    font-size: 1.1rem;
    margin: 0;
  }
`;

const Badge = styled.span`
  background: #0f7534;
  border-radius: 3px;
  font-size: 0.7rem;
  padding: 0.15rem 0.4rem;
  text-transform: uppercase;
`;

const Summary = styled.span`
  color: #ccc;
  font-size: 0.85rem;
  margin-left: auto;
`;

const Block = styled.div`
  margin-bottom: 1.25rem;

  h3 {
    color: #ccc;
    font-size: 0.85rem;
    margin: 0 0 0.5rem;
  }
`;

const Grid = styled.div`
  display: grid;
  gap: 0.75rem;
  grid-template-columns: repeat(auto-fill, minmax(96px, 1fr));
`;

const AvatarCell = styled.div<{ sold: boolean }>`
  font-size: 0.75rem;
  opacity: ${(props) => (props.sold ? 1 : 0.55)};
  text-align: center;

  img {
    aspect-ratio: 2 / 3;
    border: 1px solid ${(props) => (props.sold ? "#9dff8c" : "#555")};
    border-radius: 4px;
    display: block;
    object-fit: cover;
    object-position: top center;
    width: 100%;
  }
`;

const SalesCount = styled.div<{ sold: boolean }>`
  color: ${(props) => (props.sold ? "#9dff8c" : "#888")};
  font-weight: ${(props) => (props.sold ? "bold" : "normal")};
  margin-top: 0.25rem;
`;

const DiceRow = styled.div`
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
`;

/* Aperçu du dé tel qu'il apparaît en jeu : pastilles de `color` sur `background`. */
const DieFace = styled.div<{ background: string }>`
  align-content: center;
  background: ${(props) => props.background};
  border: 1px solid #666;
  border-radius: 6px;
  display: grid;
  gap: 3px;
  grid-template-columns: repeat(3, 1fr);
  height: 44px;
  padding: 5px;
  width: 44px;
`;

const Pip = styled.span<{ color: string; on: boolean }>`
  background: ${(props) => (props.on ? props.color : "transparent")};
  border-radius: 50%;
  height: 8px;
  width: 8px;
`;

// Face de 5 : les coins et le centre
const FIVE = [true, false, true, false, true, false, true, false, true];

const num = (n: number) => n.toLocaleString("fr-FR");

const SeasonBlock: FC<{ row: SeasonSales }> = ({ row }) => (
  <Panel>
    <SeasonHeader>
      <h2>Saison {row.season}</h2>
      {row.isCurrent && <Badge>en cours</Badge>}
      <Summary>
        {num(row.avatarSales)} avatar(s) · {num(row.diceSales)} dé(s) ·{" "}
        {num(row.revenue)} dents
      </Summary>
    </SeasonHeader>

    <Block>
      <h3>Dé de la saison</h3>
      {row.dice.length === 0 ? (
        <em>Aucun dé pour cette saison.</em>
      ) : (
        <DiceRow>
          {row.dice.map((d) => (
            <div key={d.id}>
              <DieFace background={d.background} title={`${d.color} sur ${d.background}`}>
                {FIVE.map((on, i) => (
                  <Pip key={i} color={d.color} on={on} />
                ))}
              </DieFace>
              <div style={{ fontSize: "0.75rem", marginTop: "0.25rem" }}>
                {d.id}
                <br />
                <strong>{num(d.sales)}</strong> vente(s) · {num(d.price)} dents
              </div>
            </div>
          ))}
        </DiceRow>
      )}
    </Block>

    <Block>
      <h3>Avatars de la saison</h3>
      {row.avatars.length === 0 ? (
        <em>Aucun avatar déclaré pour cette saison.</em>
      ) : (
        <Grid>
          {row.avatars.map((a) => {
            const sold = a.sales > 0;
            return (
              <AvatarCell key={a.itemKey} sold={sold}>
                <img
                  src={publicPath(
                    `${getSeasonAvatarPath(row.season)}/${a.file}`
                  )}
                  alt={a.file}
                  loading="lazy"
                />
                <SalesCount sold={sold}>
                  {num(a.sales)} vente{a.sales > 1 ? "s" : ""}
                </SalesCount>
              </AvatarCell>
            );
          })}
        </Grid>
      )}
    </Block>
  </Panel>
);

export const SalesAdmin: FC = () => {
  const isAdmin = useIsAdmin();
  const { loading, error, data } = useQuery(GET_SEASONAL_SALES_ADMIN, {
    skip: !isAdmin,
    fetchPolicy: "cache-and-network",
  });

  // Garde côté client. Le résolveur applique la même vérification côté serveur,
  // donc atteindre l'URL directement ne donne accès à aucune donnée.
  if (!isAdmin) {
    return (
      <StyledChartContainer>
        <StyledSection>
          <h1>Accès non autorisé</h1>
          <p>Vous n'avez pas la permission de voir cette page.</p>
        </StyledSection>
      </StyledChartContainer>
    );
  }

  if (loading && !data) return <Loading />;
  if (error) return <Error error={error} />;

  const rows: SeasonSales[] = data?.seasonalSalesAdmin || [];

  return (
    <Wrapper>
      <H1>Ventes par saison (Admin)</H1>
      {rows.length === 0 && <Panel>Aucune saison à afficher.</Panel>}
      {rows.map((row) => (
        <SeasonBlock key={row.season} row={row} />
      ))}
    </Wrapper>
  );
};

export default SalesAdmin;
